import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Chatbot } from '../components/Chatbot';
import { QuotaProvider } from '../hooks/useQuotaUsage';
import { QuotaBanner } from '../components/QuotaBanner';
import { UpgradeModal } from '../components/UpgradeModal';
import { MascotSmall } from '../components/Mascot';
import { EspaceOverview, ScorePanel } from '../components/EspaceOverview';
import { trackEvent } from '../lib/gamification';
import { dashboardCoreBlocks, dashboardNavItems } from '../lib/dashboardContent';
import { getStoredTaxAnswers, TAX_QUIZ_KEY } from '../lib/quizResults';
import { 
  LogOut, Bell, FolderOpen, CreditCard, Key, HelpCircle,
  ListTodo, ChevronRight, Sun, Moon, FileSearch,
  ListChecks, MessageCircle, ClipboardList, UserRound
} from 'lucide-react';

// Sidebar Navigation Item
const NavItem = ({ icon: Icon, label, path, isActive, onClick, isDark }) => (
  <motion.button
    onClick={onClick}
    data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 group ${
      isActive 
        ? isDark 
          ? 'bg-white/10 text-white border-l-2 border-[#81d8d0]' 
          : 'bg-[#81d8d0]/10 text-gray-900 border-l-2 border-[#81d8d0]'
        : isDark 
          ? 'text-[#94A3B8] hover:bg-white/5 hover:text-white' 
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`}
    whileHover={{ x: 4 }}
    whileTap={{ scale: 0.98 }}
  >
    <Icon size={20} strokeWidth={1.5} className={isActive ? 'text-[#81d8d0]' : 'group-hover:text-[#81d8d0]'} />
    <span className="font-medium text-sm">{label}</span>
  </motion.button>
);

const ActionCard = ({ title, subtitle, icon: Icon, onClick, testId, isDark }) => (
    <motion.button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`w-full min-h-48 text-left border rounded-2xl p-6 transition-all ${
        isDark
          ? 'bg-white/5 border-white/10 hover:border-[#81d8d0]/40'
          : 'bg-white border-gray-200 hover:border-[#81d8d0]/40 hover:shadow-lg'
      }`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.01 }}
    >
      <span className={`inline-flex p-2.5 rounded-xl mb-5 ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
        <Icon size={22} strokeWidth={1.5} className="text-[#81d8d0]" />
      </span>
      <span className={`block text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</span>
      <span className={`block text-sm leading-6 ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>{subtitle}</span>
      <span className="flex items-center gap-1 mt-5 text-[#81d8d0] text-sm font-medium">
        Ouvrir
        <ChevronRight size={16} />
      </span>
    </motion.button>
);

// Skipya Logo/Icon with Robot Mascot
const SkipyaIcon = () => (
  <MascotSmall className="w-10 h-10" />
);

const coreBlockIcons = {
  Aides: HelpCircle,
  Declarations: FileSearch,
  Abonnements: CreditCard,
};

const dashboardNavIcons = {
  Copilote: MessageCircle,
  'Démarches': ClipboardList,
  'Mon profil': UserRound,
  'Mes actions': ListTodo,
  Documents: FolderOpen,
  Configuration: Key,
};

// Theme Toggle
const ThemeToggle = ({ isDark, toggleTheme }) => (
  <motion.button
    onClick={toggleTheme}
    className={`p-2.5 rounded-xl transition-all duration-300 ${
      isDark 
        ? 'bg-white/5 hover:bg-white/10 text-yellow-400' 
        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
    }`}
    whileTap={{ scale: 0.95 }}
    data-testid="theme-toggle"
  >
    {isDark ? <Sun size={20} strokeWidth={1.5} /> : <Moon size={20} strokeWidth={1.5} />}
  </motion.button>
);

const DashboardPageInner = () => {
  const { user, token, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [espace, setEspace] = useState(null);

  useEffect(() => {
    if (!token) return;
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    const api = process.env.REACT_APP_BACKEND_URL;
    const loadEspace = async () => {
      try {
        // Un pré-diagnostic fait avant l'inscription est d'abord rattaché au compte.
        const pending = getStoredTaxAnswers(sessionStorage);
        if (pending) {
          await axios.put(`${api}/api/score`, { answers: pending }, auth);
          sessionStorage.removeItem(TAX_QUIZ_KEY);
        }
        const { data } = await axios.get(`${api}/api/espace`, auth);
        setEspace(data);
      } catch {
        setEspace(null);
      }
    };
    loadEspace();
  }, [token]);

  // Track first_login (idempotent server-side) + bump daily streak on dashboard mount
  useEffect(() => {
    if (token) trackEvent(token, 'first_login');
  }, [token]);

  const navItems = dashboardNavItems.map((item) => ({
    ...item,
    icon: dashboardNavIcons[item.label] || ListChecks,
  }));

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className={`min-h-screen flex transition-colors duration-300 ${isDark ? 'bg-[#0F0F1A]' : 'bg-[#FAFBFC]'}`} data-testid="dashboard-page">
      {/* Sidebar - Desktop */}
      <aside className={`hidden lg:flex flex-col w-[280px] backdrop-blur-xl border-r p-6 transition-colors duration-300 ${
        isDark ? 'bg-[#1A1A2E]/50 border-white/5' : 'bg-white/80 border-gray-200'
      }`}>
        {/* Logo */}
        <div className="flex items-center gap-3 mb-10">
          <SkipyaIcon />
          <span className={`text-xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-stone-900'}`}>
            <span className="text-[#81d8d0]">Skip</span>ya
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1">
          <p className={`text-xs font-semibold tracking-[0.1em] uppercase mb-3 px-4 ${isDark ? 'text-[#64748B]' : 'text-gray-400'}`}>Menu</p>
          {navItems.map((item) => (
            <NavItem
              key={item.path}
              icon={item.icon}
              label={item.label}
              path={item.path}
              isActive={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              isDark={isDark}
            />
          ))}
        </nav>

        {/* User section */}
        <div className={`pt-6 border-t ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#81d8d0] to-[#4ECDC4] flex items-center justify-center text-[#0F0F1A] font-bold">
              {user?.first_name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user?.first_name} {user?.last_name}</p>
              <p className={`text-xs ${isDark ? 'text-[#64748B]' : 'text-gray-500'}`}>{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className={`w-full flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all duration-300 ${
              isDark 
                ? 'text-[#94A3B8] hover:text-[#FF6B6B] hover:bg-[#FF6B6B]/10' 
                : 'text-gray-600 hover:text-[#FF6B6B] hover:bg-[#FF6B6B]/10'
            }`}
          >
            <LogOut size={18} strokeWidth={1.5} />
            <span className="text-sm font-medium">Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <QuotaBanner />
        {/* Top Header */}
        <header className={`sticky top-0 z-40 backdrop-blur-xl border-b transition-colors duration-300 ${
          isDark ? 'bg-[#0F0F1A]/80 border-white/5' : 'bg-white/80 border-gray-200'
        }`}>
          <div className="flex items-center justify-between px-6 py-4">
            {/* Mobile Logo */}
            <div className="flex items-center gap-3 lg:hidden">
              <SkipyaIcon />
              <span className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-stone-900'}`}>
                <span className="text-[#81d8d0]">Skip</span>ya
              </span>
            </div>

            {/* Welcome - Desktop */}
            <div className="hidden lg:block">
              <h1 className={`text-2xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Bonjour, {user?.first_name || 'toi'} 👋
              </h1>
          <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>
            Ton espace administratif : démarches, documents, rappels et copilote.
          </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <ThemeToggle isDark={isDark} toggleTheme={toggleTheme} />
              
              {/* Documentation button */}
              <button 
                onClick={() => navigate('/documentation')}
                className={`p-2.5 rounded-xl transition-all duration-300 ${
                  isDark 
                    ? 'bg-white/5 text-[#94A3B8] hover:text-[#81d8d0] hover:bg-white/10' 
                    : 'bg-gray-100 text-gray-600 hover:text-[#81d8d0] hover:bg-gray-200'
                }`}
                data-testid="help-btn"
                title="Documentation & Aide"
              >
                <HelpCircle size={20} strokeWidth={1.5} />
              </button>
              
              <button 
                className={`relative p-2.5 rounded-xl transition-all duration-300 ${
                  isDark 
                    ? 'bg-white/5 text-[#94A3B8] hover:text-white hover:bg-white/10' 
                    : 'bg-gray-100 text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                }`}
                data-testid="notifications-btn"
              >
                <Bell size={20} strokeWidth={1.5} />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#FF6B6B] rounded-full" />
              </button>
              
              {/* Mobile User Avatar */}
              <div className="lg:hidden w-9 h-9 rounded-full bg-gradient-to-br from-[#81d8d0] to-[#4ECDC4] flex items-center justify-center text-[#0F0F1A] font-bold text-sm">
                {user?.first_name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
            </div>
          </div>

          {/* Mobile Welcome */}
          <div className="lg:hidden px-6 pb-4">
            <h1 className={`text-xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Bonjour, {user?.first_name || 'toi'} 👋
            </h1>
            <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>Votre copilote administratif</p>
          </div>
        </header>

        {/* Copilot environment */}
        <div className="p-6">
          <EspaceOverview espace={espace} />

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-4 lg:gap-6 mb-6">
            <section
              className={`${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-2xl p-4 md:p-5`}
              data-testid="copilot-main-panel"
            >
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <p className="text-[#81d8d0] text-sm font-semibold mb-1">Copilote principal</p>
                  <h2 className={`text-2xl md:text-3xl font-semibold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Dis-moi ce que tu veux régler, SKIPYA lance les bons outils.
                  </h2>
                </div>
              </div>
              <Chatbot mode="panel" />
            </section>

            <aside className={`${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-2xl p-5`}>
              <ScorePanel espace={espace} />
            </aside>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 pb-24 lg:pb-6">
            {dashboardCoreBlocks.map((block) => (
              <ActionCard
                key={block.title}
                title={block.title}
                subtitle={block.subtitle}
                icon={coreBlockIcons[block.title]}
                onClick={() => navigate(block.path)}
                testId={block.testId}
                isDark={isDark}
              />
            ))}
          </div>
        </div>

        {/* Mobile Bottom Navigation */}
        <nav className={`lg:hidden fixed bottom-0 left-0 right-0 backdrop-blur-xl border-t px-2 py-2 safe-area-pb transition-colors duration-300 ${
          isDark ? 'bg-[#1A1A2E]/90 border-white/5' : 'bg-white/90 border-gray-200'
        }`}>
          <div className="flex justify-around">
            {navItems.slice(0, 5).map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={`flex flex-col items-center p-2 rounded-xl transition-all ${
                  location.pathname === item.path 
                    ? 'text-[#81d8d0]' 
                    : isDark ? 'text-[#64748B] hover:text-white' : 'text-gray-400 hover:text-gray-900'
                }`}
              >
                <item.icon size={22} strokeWidth={1.5} />
                <span className="text-[10px] mt-1 font-medium">{item.label}</span>
              </button>
            ))}
          </div>
        </nav>
      </main>

    </div>
  );
};

export const DashboardPage = () => (
  <QuotaProvider>
    <DashboardPageInner />
    <UpgradeModal />
  </QuotaProvider>
);

export default DashboardPage;
